/**
 * Coerce an unknown value (often a thrown error or API payload) into a
 * human-readable string, falling back when the value carries no usable text.
 *
 * Consolidates duplicate helpers previously named `toDisplayText`,
 * `getRenderableText`, and `getErrorMessage` scattered across queries/hooks.
 */
export function coerceToString(value: unknown, fallback: string): string {
  if (typeof value === "string") {
    return value;
  }

  if (value instanceof Error) {
    return value.message || fallback;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (value && typeof value === "object" && "detail" in value) {
    const detail = (value as { detail?: unknown }).detail;
    const detailStr = coerceFastapiDetail(detail);
    if (detailStr) return detailStr;
  }

  if (value && typeof value === "object" && "message" in value) {
    const message = (value as { message?: unknown }).message;
    if (typeof message === "string") {
      return message;
    }
    if (message instanceof Error) {
      return message.message || fallback;
    }
  }

  try {
    const serialized = JSON.stringify(value);
    return serialized && serialized !== "{}" ? serialized : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Extract a readable string from a FastAPI error payload. Handles both
 * the simple `{ detail: "..." }` shape (HTTPException) and the
 * validation-error list `{ detail: [{ loc, msg, type }, ...] }`.
 */
function coerceFastapiDetail(detail: unknown): string | null {
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((item) => {
        if (item && typeof item === "object" && "msg" in item) {
          const msg = (item as { msg?: unknown; loc?: unknown }).msg;
          const loc = (item as { loc?: unknown }).loc;
          if (typeof msg === "string") {
            const locStr =
              Array.isArray(loc) && loc.length > 0
                ? ` (${loc.join(".")})`
                : "";
            return `${msg}${locStr}`;
          }
        }
        return null;
      })
      .filter((m): m is string => Boolean(m));
    if (msgs.length > 0) return msgs.join("; ");
  }
  return null;
}
